import { useEffect, useState } from "react";

const STORAGE_KEY = "pharuerp:access-session";

// Cache de object URLs por `src`, COMPARTILHADO entre todas as instancias do
// hook (Header, Perfil, UserAvatar costumam usar a mesma foto_url). Antes, cada
// montagem refazia o fetch autenticado (Render free => ~2s) e ainda revogava o
// blob no unmount, forcando novo fetch a cada navegacao — por isso o Perfil
// mostrava as iniciais e so depois a foto "piscava" por cima.
//
// Com o cache: a 1a montagem (normalmente o avatar do Header, sempre presente)
// busca uma vez; as montagens seguintes pegam o blob na hora, sem flash. Nao
// revogamos no unmount — os blobs vivem ate o logout (clearAuthenticatedImageCache).
const objectUrlCache = new Map(); // src -> objectURL
const inflightCache = new Map(); // src -> Promise<objectURL> (dedup de fetch concorrente)

function readAccessToken() {
  if (typeof window === "undefined") return "";

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw)?.accessToken || "" : "";
  } catch {
    return "";
  }
}

async function carregarObjectUrl(src) {
  if (objectUrlCache.has(src)) return objectUrlCache.get(src);
  if (inflightCache.has(src)) return inflightCache.get(src);

  const promise = (async () => {
    const token = readAccessToken();
    const response = await fetch(src, {
      // no-store: o unico fetch real (1x por src/sessao) sempre vem fresco do
      // backend. A camada de reuso e o objectUrlCache acima, que controlamos —
      // assim nao dependemos do cache HTTP do navegador (e sua janela de 60s).
      cache: "no-store",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      throw new Error(`Falha ao carregar imagem (${response.status})`);
    }

    const contentType = response.headers.get("Content-Type") || "";
    const blob = await response.blob();
    const normalizedType = (contentType || blob.type || "").toLowerCase();

    if (
      !normalizedType.startsWith("image/") &&
      /^(text\/html|text\/plain|application\/json)/i.test(normalizedType)
    ) {
      throw new Error("A resposta da foto não e uma imagem.");
    }

    if (!blob.size) {
      throw new Error("A resposta da foto está vazia.");
    }

    const objectUrl = URL.createObjectURL(blob);
    objectUrlCache.set(src, objectUrl);
    return objectUrl;
  })();

  inflightCache.set(src, promise);
  try {
    return await promise;
  } finally {
    inflightCache.delete(src);
  }
}

/**
 * Invalida o cache de imagens autenticadas e revoga os object URLs (libera
 * memoria). Sem `src`, limpa tudo — chamado por clearSession (logout), tambem
 * para evitar que a foto de um usuario apareca para outro num terminal
 * compartilhado. Com `src`, remove apenas aquela entrada.
 */
export function clearAuthenticatedImageCache(src) {
  if (src) {
    const url = objectUrlCache.get(src);
    if (url) URL.revokeObjectURL(url);
    objectUrlCache.delete(src);
    inflightCache.delete(src);
    return;
  }

  objectUrlCache.forEach((url) => URL.revokeObjectURL(url));
  objectUrlCache.clear();
  inflightCache.clear();
}

export function useAuthenticatedImageUrl(src) {
  // Init sincrono a partir do cache: se a foto ja foi buscada nesta sessao
  // (ex.: o avatar do Header montou antes), o Perfil ja nasce com a imagem —
  // sem o "pisca iniciais -> foto".
  const [imageUrl, setImageUrl] = useState(() => {
    if (!src) return "";
    if (/^(data:|blob:)/i.test(src)) return src;
    return objectUrlCache.get(src) || "";
  });
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) {
      setImageUrl("");
      setFailed(false);
      return undefined;
    }

    if (/^(data:|blob:)/i.test(src)) {
      setImageUrl(src);
      setFailed(false);
      return undefined;
    }

    const cached = objectUrlCache.get(src);
    if (cached) {
      setImageUrl(cached);
      setFailed(false);
      return undefined;
    }

    let active = true;
    setFailed(false);

    carregarObjectUrl(src)
      .then((url) => {
        if (active) {
          setImageUrl(url);
          setFailed(false);
        }
      })
      .catch(() => {
        if (active) {
          setImageUrl("");
          setFailed(true);
        }
      });

    return () => {
      active = false;
    };
  }, [src]);

  return { imageUrl, failed };
}
